import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Textarea } from '@/shared/components/ui/textarea';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { toast } from 'sonner';
import {
  PenSquare,
  Plus,
  Search,
  Filter,
  MoreVertical,
  Image,
  Video,
  FileText,
  Calendar,
  Send,
  Eye,
  Edit,
  Trash2,
  Copy,
  Instagram,
  Facebook,
  Linkedin,
  Youtube,
  Twitter,
  CheckCircle,
  Clock,
  AlertCircle,
  Sparkles,
  Wand2 } from
'lucide-react';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const statusColors = {
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  pending_review: 'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
  archived: 'bg-gray-100 text-gray-500 border-gray-200'
};

const statusIcons = {
  draft: Edit,
  pending_review: Clock,
  approved: CheckCircle,
  rejected: AlertCircle,
  archived: FileText
};

export const ContentStudio = () => {const { t } = useTranslation("cns");
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [contentIdeas, setContentIdeas] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiConfig, setAiConfig] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiTone, setAiTone] = useState('professional');
  const [aiPlatform, setAiPlatform] = useState('');
  const [newContent, setNewContent] = useState({
    title: '',
    description: '',
    canonical_text: '',
    tags: []
  });

  useEffect(() => {
    fetchWorkspaces();
    fetchAIConfig();
  }, []);

  useEffect(() => {
    if (selectedWorkspace) {
      fetchContent(selectedWorkspace.id);
    }
  }, [selectedWorkspace, statusFilter]);

  const fetchWorkspaces = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cns/workspaces`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setWorkspaces(data.workspaces || []);
        if (data.workspaces?.length > 0) {
          setSelectedWorkspace(data.workspaces[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch workspaces');
    } finally {
      setLoading(false);
    }
  };

  const fetchAIConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/cns/ai/config`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setAiConfig(data);
      }
    } catch (err) {
      console.error('Failed to fetch AI config');
    }
  };

  const generateWithAI = async () => {
    if (!aiPrompt) {
      toast.error('Please enter a prompt');
      return;
    }

    if (!aiConfig?.is_configured) {
      toast.error('AI not configured. Go to CNS > AI Configuration to set up.');
      return;
    }

    setAiGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/cns/ai/generate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          prompt: aiPrompt,
          content_type: 'social_post',
          platform: aiPlatform || null,
          tone: aiTone,
          max_tokens: 500,
          temperature: 0.7
        })
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setNewContent({
            ...newContent,
            canonical_text: data.content,
            description: `Generated with ${data.provider}/${data.model}`
          });
          toast.success('Content generated!');
          setShowAIModal(false);
          setShowCreateModal(true);
        } else {
          toast.error('AI generation failed');
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'AI generation failed');
      }
    } catch (err) {
      toast.error('Failed to generate content');
    } finally {
      setAiGenerating(false);
    }
  };

  const fetchContent = async (workspaceId) => {
    try {
      let url = `${API_URL}/api/cns/workspaces/${workspaceId}/content`;
      if (statusFilter !== 'all') {
        url += `?status=${statusFilter}`;
      }
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setContentIdeas(data.content_ideas || []);
      }
    } catch (err) {
      console.error('Failed to fetch content');
    }
  };

  const createContent = async () => {
    if (!selectedWorkspace || !newContent.title) {
      toast.error('Please enter a title');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/cns/workspaces/${selectedWorkspace.id}/content`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newContent)
      });
      if (res.ok) {
        const content = await res.json();
        toast.success('Content created!');
        setContentIdeas([content, ...contentIdeas]);
        setShowCreateModal(false);
        setNewContent({ title: '', description: '', canonical_text: '', tags: [] });
      }
    } catch (err) {
      toast.error('Failed to create content');
    }
  };

  const filteredContent = contentIdeas.filter((content) =>
  content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
  content.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="content-studio">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl">
              <PenSquare className="h-7 w-7 text-white" />
            </div>{t("contentStudio", "Content Studio")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("createAndManage", "Create and manage your canonical content across all channels")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setShowAIModal(true)}>
            
            <Sparkles className="h-4 w-4" />{t("aiGenerate", "AI Generate")}

            {aiConfig?.is_configured &&
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            }
          </Button>
          <Button
            className="gap-2 bg-gradient-to-r from-pink-500 to-rose-600"
            onClick={() => setShowCreateModal(true)}>
            
            <Plus className="h-4 w-4" />{t("newContent", "New Content")}

          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10" />
          
        </div>
        <Tabs value={statusFilter} onValueChange={setStatusFilter} className="w-full sm:w-auto">
          <TabsList>
            <TabsTrigger value="all">{t("all", "All")}</TabsTrigger>
            <TabsTrigger value="draft">{t("draft", "Draft")}</TabsTrigger>
            <TabsTrigger value="pending_review">{t("pending", "Pending")}</TabsTrigger>
            <TabsTrigger value="approved">{t("approved", "Approved")}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Content Grid */}
      {filteredContent.length === 0 ?
      <Card className="card-elevated border-dashed">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-pink-100 to-rose-100 flex items-center justify-center">
              <PenSquare className="h-10 w-10 text-pink-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">{t("noContentYet", "No Content Yet")}</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("createYourFirst", "Create your first piece of content to start publishing across all your social channels.")}

          </p>
            <Button
            className="gap-2 bg-gradient-to-r from-pink-500 to-rose-600"
            onClick={() => setShowCreateModal(true)}>
            
              <Plus className="h-4 w-4" />{t("createContent", "Create Content")}

          </Button>
          </CardContent>
        </Card> :

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredContent.map((content) => {
          const StatusIcon = statusIcons[content.status] || Edit;
          return (
            <Card key={content.id} className="card-elevated hover:shadow-lg transition-shadow cursor-pointer">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <Badge className={statusColors[content.status]}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {content.status.replace('_', ' ')}
                    </Badge>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </div>
                  <CardTitle className="text-lg mt-2">{content.title}</CardTitle>
                  <CardDescription className="line-clamp-2">
                    {content.description || content.canonical_text}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <span>{content.total_variants || 0}{t("variants", "variants")}</span>
                      <span>•</span>
                      <span>{content.total_published || 0}{t("published", "published")}</span>
                    </div>
                    <div className="flex -space-x-1">
                      {['instagram', 'facebook', 'linkedin'].slice(0, 3).map((platform) =>
                    <div key={platform} className="w-6 h-6 rounded-full bg-muted flex items-center justify-center border-2 border-background">
                          {platform === 'instagram' && <Instagram className="h-3 w-3" />}
                          {platform === 'facebook' && <Facebook className="h-3 w-3" />}
                          {platform === 'linkedin' && <Linkedin className="h-3 w-3" />}
                        </div>
                    )}
                    </div>
                  </div>
                  {content.tags?.length > 0 &&
                <div className="flex flex-wrap gap-1 mt-3">
                      {content.tags.slice(0, 3).map((tag) =>
                  <Badge key={tag} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                  )}
                    </div>
                }
                </CardContent>
              </Card>);

        })}
        </div>
      }

      {/* Create Modal */}
      {showCreateModal &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wand2 className="h-5 w-5 text-pink-500" />{t("createNewContent", "Create New Content")}

            </CardTitle>
              <CardDescription>{t("createYourCanonical", "Create your canonical content that will be adapted for each channel")}

            </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t("title", "Title")}</label>
                <Input
                placeholder="Enter content title..."
                value={newContent.title}
                onChange={(e) => setNewContent({ ...newContent, title: e.target.value })} />
              
              </div>
              <div>
                <label className="text-sm font-medium">{t("description", "Description")}</label>
                <Input
                placeholder="Brief description..."
                value={newContent.description}
                onChange={(e) => setNewContent({ ...newContent, description: e.target.value })} />
              
              </div>
              <div>
                <label className="text-sm font-medium">{t("content", "Content")}</label>
                <Textarea
                placeholder="Write your main content here..."
                value={newContent.canonical_text}
                onChange={(e) => setNewContent({ ...newContent, canonical_text: e.target.value })}
                rows={6} />
              
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>{t("cancel", "Cancel")}

              </Button>
                <Button
                className="bg-gradient-to-r from-pink-500 to-rose-600"
                onClick={createContent}>{t("createContent", "Create Content")}


              </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }

      {/* AI Generation Modal */}
      {showAIModal &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl mx-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-violet-500" />{t("aiContentGenerator", "AI Content Generator")}

            </CardTitle>
              <CardDescription>
                {aiConfig?.is_configured ?
              `Using ${aiConfig.provider} / ${aiConfig.model}` :
              'AI not configured - Go to CNS > AI Configuration to set up'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium">{t("whatDoYou", "What do you want to create?")}</label>
                <Textarea
                placeholder="Describe what content you want to generate. E.g., 'Write an engaging post about our new product launch for skincare enthusiasts'"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                rows={4} />
              
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium">{t("tone", "Tone")}</label>
                  <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={aiTone}
                  onChange={(e) => setAiTone(e.target.value)}>
                  
                    <option value="professional">{t("professional", "Professional")}</option>
                    <option value="casual">{t("casual", "Casual")}</option>
                    <option value="humorous">{t("humorous", "Humorous")}</option>
                    <option value="inspiring">{t("inspiring", "Inspiring")}</option>
                    <option value="educational">{t("educational", "Educational")}</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium">{t("targetPlatformOptional", "Target Platform (Optional)")}</label>
                  <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={aiPlatform}
                  onChange={(e) => setAiPlatform(e.target.value)}>
                  
                    <option value="">{t("general", "General")}</option>
                    <option value="instagram">{t("instagram", "Instagram")}</option>
                    <option value="facebook">{t("facebook", "Facebook")}</option>
                    <option value="linkedin">{t("linkedin", "LinkedIn")}</option>
                    <option value="x">{t("xTwitter", "X (Twitter)")}</option>
                    <option value="tiktok">{t("tiktok", "TikTok")}</option>
                  </select>
                </div>
              </div>
              {!aiConfig?.is_configured &&
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-700 text-sm">
                  <AlertCircle className="h-4 w-4 inline mr-2" />{t("pleaseConfigureAi", "Please configure AI in Settings first. Supports Ollama (local), OpenRouter, Anthropic, Gemini, and OpenAI.")}

            </div>
            }
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowAIModal(false)}>{t("cancel", "Cancel")}

              </Button>
                <Button
                className="gap-2 bg-gradient-to-r from-violet-500 to-purple-600"
                onClick={generateWithAI}
                disabled={aiGenerating || !aiConfig?.is_configured}>
                
                  {aiGenerating ?
                <>
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />{t("generating", "Generating...")}

                </> :

                <>
                      <Wand2 className="h-4 w-4" />{t("generateContent", "Generate Content")}

                </>
                }
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      }
    </div>);

};

export default ContentStudio;